/**
 * CameraCapture : accès caméra unifié (getUserMedia) compatible Safari/Chrome iPad (WebKit).
 * - vidéo autoplay+muted+playsinline, play() explicite après affectation du flux ;
 * - contraintes sans `exact` (repli sur { video: true }) ; démarrage sur geste utilisateur ;
 * - clic/toucher n'importe où sur l'aperçu = capture, avec flash blanc ;
 * - repli « Prendre une photo » (appareil photo natif) et « Importer » ; pistes arrêtées à la fermeture.
 * Appelle onResult(base64, previewDataUrl) à chaque capture/import.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Camera, ImageUp, Play } from 'lucide-react';
import { captureFromVideo, fileToBase64, fileToPreview } from '../../utils/image';
import { Button } from '../../components/ui/kit';
import { CAMERA_CONSTRAINTS, CAPTURE_FLASH_MS } from '../../constants';

/** Message d'erreur lisible selon la cause de l'échec caméra. */
function cameraErrorMessage(err) {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return "Accès caméra refusé. Autorisez la caméra, ou utilisez « Prendre une photo ».";
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return "Aucune caméra détectée. Utilisez « Prendre une photo » ou importez une image.";
  if (name === 'NotReadableError') return "Caméra occupée par une autre application. Utilisez « Prendre une photo ».";
  return "Aperçu caméra indisponible ici. Utilisez « Prendre une photo » (appareil photo) ou importez une image.";
}

export default function CameraView({ onResult, burstActive = false, burstCount = 0, captureLabel = 'Capturer', compact = false }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [flash, setFlash] = useState(false);

  const stop = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  // Démarre la caméra (suite à un geste utilisateur). Repli sur { video: true } si les contraintes échouent.
  const start = useCallback(async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(cameraErrorMessage({ name: 'NotFoundError' }));
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
    } catch (e1) {
      try { stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false }); }
      catch (e2) { setError(cameraErrorMessage(e2)); return; }
    }
    streamRef.current = stream;
    const v = videoRef.current;
    if (v) {
      v.srcObject = stream;
      try { await v.play(); } catch { /* lecture différée : sans conséquence si autoplay OK */ }
      setReady(true);
    }
  }, []);

  // Tentative de démarrage automatique au montage ; l'overlay « Activer » sert de repli au geste.
  useEffect(() => { start(); return () => stop(); }, [start, stop]);

  const capture = () => {
    if (!videoRef.current || !ready) return;
    setFlash(true);
    setTimeout(() => setFlash(false), CAPTURE_FLASH_MS);
    const b64 = captureFromVideo(videoRef.current);
    onResult(b64, `data:image/jpeg;base64,${b64}`);
  };

  const handleFile = async (file) => {
    if (!file) return;
    const preview = await fileToPreview(file);
    const b64 = await fileToBase64(file);
    onResult(b64, preview);
  };

  return (
    <div data-testid="camera-view">
      <div
        className={`relative vd-plate ${compact ? 'aspect-video' : 'aspect-[4/3]'} w-full overflow-hidden ${dragOver ? 'ring-2 ring-gold' : ''} ${ready ? 'cursor-pointer' : ''}`}
        onClick={ready ? capture : undefined}
        role={ready ? 'button' : undefined}
        aria-label={ready ? 'Capturer la photo' : undefined}
        data-testid="camera-zone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
      >
        <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover rounded bg-black/5" />
        {/* Flash blanc à la capture */}
        <div className="absolute inset-0 bg-white pointer-events-none rounded" style={{ opacity: flash ? 0.85 : 0, transition: `opacity ${CAPTURE_FLASH_MS}ms ease-out` }} />
        {!ready && (
          <div className="absolute inset-0 vd-hatch flex flex-col items-center justify-center text-center gap-3 px-6 rounded text-[13px]" data-testid="camera-status">
            <span>{error || 'Activation de la caméra…'}</span>
            <button onClick={(e) => { e.stopPropagation(); start(); }} className="vd-btn vd-btn-secondary" data-testid="camera-start-btn">
              <Play className="w-4 h-4" strokeWidth={1.6} /> Activer la caméra
            </button>
          </div>
        )}
        {burstActive && (
          <div className="absolute top-3 left-3 flex items-center gap-2 bg-paper/90 px-3 py-1 rounded-full text-[13px]" data-testid="burst-banner">
            <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" /> Rafale active
            <span className="nums text-n600">· {burstCount}</span>
          </div>
        )}
        {ready && <span className="absolute bottom-2 right-3 text-[11px] text-paper/90 bg-ink/40 px-2 py-0.5 rounded-full pointer-events-none">Toucher pour capturer</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {ready ? (
          <Button variant="primary" onClick={capture} data-testid="camera-capture-btn">
            <Camera className="w-4 h-4" strokeWidth={1.6} /> {captureLabel}
          </Button>
        ) : (
          <label className="vd-btn vd-btn-primary cursor-pointer" data-testid="camera-native-capture-btn">
            <Camera className="w-4 h-4" strokeWidth={1.6} /> Prendre une photo
            <input type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])} />
          </label>
        )}
        <label className="vd-btn vd-btn-secondary cursor-pointer" data-testid="camera-import-btn">
          <ImageUp className="w-4 h-4" strokeWidth={1.6} /> Importer une photo ou un PDF
          <input type="file" accept="image/*,application/pdf" className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );
}
